import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import { listUsers, type UserListResponse } from '@/shared/api/users';
import { connectorKeys, dagKeys, userKeys } from '@/shared/lib/query/queryKeys';

import { type ConnectorListResponse, getConnectorsApi } from '@/entities/connector';
import { type DagDashboardListResponse, fetchDagDashboardListApi } from '@/entities/dag';

const USER_LIST_PARAMS = { page: 1, size: 1 } as const;
const DAG_DASHBOARD_LIST_PARAMS = { limit: 100, offset: 0 } as const;

export const useAdminConfigurationCounts = () => {
  const [usersQuery, connectorsQuery, dagDashboardListQuery] = useQueries({
    queries: [
      {
        queryKey: userKeys.lists(USER_LIST_PARAMS),
        queryFn: () => listUsers(USER_LIST_PARAMS),
        retry: false,
      },
      {
        queryKey: connectorKeys.lists(),
        queryFn: () => getConnectorsApi(),
        retry: false,
      },
      {
        queryKey: dagKeys.dashboardList(DAG_DASHBOARD_LIST_PARAMS),
        queryFn: () => fetchDagDashboardListApi(DAG_DASHBOARD_LIST_PARAMS),
        retry: false,
      },
    ],
  }) as [
    UseQueryResult<UserListResponse>,
    UseQueryResult<ConnectorListResponse>,
    UseQueryResult<DagDashboardListResponse>,
  ];

  const userCount = usersQuery.data?.pagination.totalCount ?? 0;
  const connectorTotalCount =
    (connectorsQuery.data?.summary.enable ?? 0) + (connectorsQuery.data?.summary.disable ?? 0);
  const dagTotalCount = dagDashboardListQuery.data?.total ?? 0;
  const isUserCountLoading = usersQuery.isLoading;
  const isConnectorCountLoading = connectorsQuery.isLoading;
  const isDagCountLoading = dagDashboardListQuery.isLoading;
  const isLoading = isUserCountLoading || isConnectorCountLoading || isDagCountLoading;
  const hasError = usersQuery.isError || connectorsQuery.isError || dagDashboardListQuery.isError;

  return {
    userCount,
    connectorTotalCount,
    dagTotalCount,
    isLoading,
    isUserCountLoading,
    isConnectorCountLoading,
    isDagCountLoading,
    hasError,
  } as const;
};
